/**
 * Vue "Progression PCED par site" : PCED validées vs objectif annuel pour
 * chaque site, triés du plus en retard au plus avancé, afin d'identifier
 * rapidement les sites en retard sur leurs objectifs. La progression ARED
 * n'est pas affichée ici pour le moment (sur demande de l'utilisateur).
 * La barre est empilée en 3 segments : vert (validées), orange (à valider,
 * déjà créées mais pas encore validées), rouge (reste à faire pour
 * atteindre l'objectif annuel).
 */
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import { Skeleton } from "@/components/ui/skeleton";

function toneFor(pct) {
  if (pct < 34) return { text: "text-red-600" };
  if (pct < 67) return { text: "text-amber-600" };
  return { text: "text-emerald-600" };
}

function segments(site) {
  const { objective, pced_validated: validated, pced_total: total } = site;
  if (!objective) return { validatedPct: 0, pendingPct: 0, remainingPct: 100 };
  const validatedPct = Math.min(validated / objective, 1) * 100;
  const remainingCapacity = 100 - validatedPct;
  const pendingRaw = Math.max(total - validated, 0);
  const pendingPct = Math.min((pendingRaw / objective) * 100, remainingCapacity);
  const remainingPct = Math.max(100 - validatedPct - pendingPct, 0);
  return { validatedPct, pendingPct, remainingPct };
}

export default function SiteProgressPanel() {
  const { filters, refreshKey } = useFilters();
  const [sites, setSites] = useState(null);

  useEffect(() => {
    dashboardApi.getSiteProgress(filters).then(({ data }) => {
      setSites([...data.sites].sort((a, b) => a.pced_pct - b.pced_pct));
    });
  }, [filters, refreshKey]);

  if (!sites) return <Skeleton className="h-64 rounded-lg" />;

  const behindCount = sites.filter((s) => s.pced_pct < 50).length;

  return (
    <div data-testid="site-progress-panel" className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-700">Progression PCED par site vs objectif annuel</h2>
        {behindCount > 0 && (
          <span data-testid="site-progress-behind-count" className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-600">
            <AlertTriangle className="h-3.5 w-3.5" /> {behindCount} site(s) à moins de 50% de l'objectif
          </span>
        )}
      </div>
      <div className="mb-3 flex items-center gap-4 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Validées</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> À valider</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> Reste à faire</span>
      </div>
      <div className="space-y-3">
        {sites.map((s) => {
          const tone = toneFor(s.pced_pct);
          const { validatedPct, pendingPct, remainingPct } = segments(s);
          const pending = Math.max(s.pced_total - s.pced_validated, 0);
          return (
            <div key={s.site} data-testid={`site-progress-row-${s.site}`} className="grid grid-cols-[140px_1fr_auto] items-center gap-3 text-xs sm:grid-cols-[160px_1fr_100px]">
              <span className="truncate font-medium text-slate-700" title={s.site}>{s.site}</span>
              <div className="flex flex-col gap-1">
                <div data-testid={`site-progress-bar-${s.site}`} className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  {validatedPct > 0 && <div className="h-full bg-emerald-500" style={{ width: `${validatedPct}%` }} />}
                  {pendingPct > 0 && <div className="h-full bg-amber-500" style={{ width: `${pendingPct}%` }} />}
                  {remainingPct > 0 && <div className="h-full bg-red-500" style={{ width: `${remainingPct}%` }} />}
                </div>
                <span className="text-[11px] text-slate-400">
                  PCED {s.pced_validated} validées{pending > 0 ? ` · ${pending} à valider` : ""} /{s.objective}
                </span>
              </div>
              <span className={`text-right font-semibold tabular-nums ${tone.text}`}>{s.pced_pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

